import { useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery } from "@tanstack/react-query";
import { Loader2, Download, Search, ShieldAlert } from "lucide-react";

import { SiteHeader } from "@/components/site/SiteHeader";
import { AdminNav } from "@/components/admin/AdminNav";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  exportMayaPreviewPlaysCsv,
  listMayaPreviewPlays,
  type MayaPreviewPlaysFilters,
} from "@/lib/maya-preview-plays.functions";

export const Route = createFileRoute("/_authenticated/admin/maya-preview-plays")({
  head: () => ({
    meta: [
      { title: "Admin · Maya preview plays — Mostrando México" },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
  component: MayaPreviewPlaysPage,
});

type FormState = {
  from: string;
  to: string;
  destination: string;
  lang: string;
  deviceId: string;
  ip: string;
};

const EMPTY: FormState = { from: "", to: "", destination: "", lang: "", deviceId: "", ip: "" };

function toFilters(f: FormState): MayaPreviewPlaysFilters {
  const iso = (v: string) => (v ? new Date(v).toISOString() : undefined);
  return {
    from: iso(f.from),
    to: iso(f.to),
    destination: f.destination.trim() || undefined,
    lang: f.lang.trim() || undefined,
    deviceId: f.deviceId.trim() || undefined,
    ip: f.ip.trim() || undefined,
    limit: 500,
  };
}

function fmtDate(iso: string): string {
  return new Date(iso).toLocaleString("es-MX", { dateStyle: "short", timeStyle: "medium" });
}

function MayaPreviewPlaysPage() {
  const list = useServerFn(listMayaPreviewPlays);
  const exportCsv = useServerFn(exportMayaPreviewPlaysCsv);

  const [form, setForm] = useState<FormState>(EMPTY);
  const [applied, setApplied] = useState<MayaPreviewPlaysFilters>({ limit: 500 });
  const [downloading, setDownloading] = useState(false);

  const query = useQuery({
    queryKey: ["maya-preview-plays", applied],
    queryFn: () => list({ data: applied }),
    retry: false,
  });

  const onApply = (e: React.FormEvent) => {
    e.preventDefault();
    setApplied(toFilters(form));
  };

  const onDownload = async () => {
    setDownloading(true);
    try {
      const res = await exportCsv({ data: applied });
      const blob = new Blob([res.csv], { type: "text/csv;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = res.filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } finally {
      setDownloading(false);
    }
  };

  const rows = query.data?.rows ?? [];
  const forbidden =
    query.error instanceof Error && /forbidden|unauthorized/i.test(query.error.message);

  return (
    <div className="min-h-screen bg-background">
      <SiteHeader />
      <div className="container mx-auto max-w-6xl space-y-6 px-4 py-8">
        <div className="space-y-3">
          <AdminNav />
          <h1 className="text-2xl font-semibold">Maya · Preview plays</h1>
          <p className="text-sm text-muted-foreground">
            Auditoría de reproducciones del preview de audio (límite 2/24h por device_id o IP).
          </p>
        </div>

        {forbidden ? (
          <div className="flex items-center gap-2 rounded-md border border-destructive/40 bg-destructive/10 p-4 text-sm text-destructive">
            <ShieldAlert className="h-4 w-4" /> No tienes permisos para ver esta sección.
          </div>
        ) : (
          <>
            <form
              onSubmit={onApply}
              className="grid grid-cols-1 gap-3 rounded-lg border bg-card p-4 md:grid-cols-3"
            >
              <div className="space-y-1">
                <Label htmlFor="from">Desde</Label>
                <Input
                  id="from"
                  type="datetime-local"
                  value={form.from}
                  onChange={(e) => setForm({ ...form, from: e.target.value })}
                />
              </div>
              <div className="space-y-1">
                <Label htmlFor="to">Hasta</Label>
                <Input
                  id="to"
                  type="datetime-local"
                  value={form.to}
                  onChange={(e) => setForm({ ...form, to: e.target.value })}
                />
              </div>
              <div className="space-y-1">
                <Label htmlFor="lang">Idioma</Label>
                <Input
                  id="lang"
                  placeholder="es, en, fr, pt, it"
                  value={form.lang}
                  onChange={(e) => setForm({ ...form, lang: e.target.value })}
                />
              </div>
              <div className="space-y-1">
                <Label htmlFor="destination">Destino (contiene)</Label>
                <Input
                  id="destination"
                  placeholder="chichen-itza"
                  value={form.destination}
                  onChange={(e) => setForm({ ...form, destination: e.target.value })}
                />
              </div>
              <div className="space-y-1">
                <Label htmlFor="deviceId">Device ID</Label>
                <Input
                  id="deviceId"
                  value={form.deviceId}
                  onChange={(e) => setForm({ ...form, deviceId: e.target.value })}
                />
              </div>
              <div className="space-y-1">
                <Label htmlFor="ip">IP</Label>
                <Input
                  id="ip"
                  value={form.ip}
                  onChange={(e) => setForm({ ...form, ip: e.target.value })}
                />
              </div>
              <div className="flex items-end gap-2 md:col-span-3">
                <Button type="submit" disabled={query.isFetching}>
                  {query.isFetching ? (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  ) : (
                    <Search className="mr-2 h-4 w-4" />
                  )}
                  Aplicar filtros
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setForm(EMPTY);
                    setApplied({ limit: 500 });
                  }}
                >
                  Limpiar
                </Button>
                <div className="flex-1" />
                <Button
                  type="button"
                  variant="secondary"
                  onClick={onDownload}
                  disabled={downloading || rows.length === 0}
                >
                  {downloading ? (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  ) : (
                    <Download className="mr-2 h-4 w-4" />
                  )}
                  Descargar CSV
                </Button>
              </div>
            </form>

            <div className="rounded-lg border bg-card">
              <div className="flex items-center justify-between border-b px-4 py-2 text-sm text-muted-foreground">
                <span>{rows.length} resultado(s) — límite 500</span>
                {query.isFetching ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-muted/40 text-left text-xs uppercase text-muted-foreground">
                    <tr>
                      <th className="px-3 py-2">Fecha</th>
                      <th className="px-3 py-2">Destino</th>
                      <th className="px-3 py-2">Lang</th>
                      <th className="px-3 py-2">Device ID</th>
                      <th className="px-3 py-2">IP</th>
                      <th className="px-3 py-2">User</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((r) => (
                      <tr key={r.id} className="border-t">
                        <td className="whitespace-nowrap px-3 py-2">{fmtDate(r.played_at)}</td>
                        <td className="px-3 py-2">{r.destination}</td>
                        <td className="px-3 py-2">{r.lang}</td>
                        <td className="px-3 py-2 font-mono text-xs">{r.device_id ?? "—"}</td>
                        <td className="px-3 py-2 font-mono text-xs">{r.ip ?? "—"}</td>
                        <td className="px-3 py-2 font-mono text-xs">{r.user_id ?? "—"}</td>
                      </tr>
                    ))}
                    {rows.length === 0 && !query.isFetching ? (
                      <tr>
                        <td colSpan={6} className="px-3 py-8 text-center text-muted-foreground">
                          Sin reproducciones para estos filtros.
                        </td>
                      </tr>
                    ) : null}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
